import { Component, HostListener, OnInit } from '@angular/core';
import {
    UserPermissionsService,
    PermissionSetPage,
    Module,
    PermissionSetUpdateForm,
} from '../../../services/user-permissions.service';
import { ToastService } from '../../../../shared/services/toast.service';
import {
    NoDataTypes,
    NoDataActionType,
    NoDataSourceType,
} from 'app/modules/shared/interfaces/no-data';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationAlertDialogComponent } from '../../confirmation-alert-dialog/confirmation-alert-dialog.component';
import { PermissionSetUsersComponent } from 'app/modules/settings/components/role-management/permission-set-users/permission-set-users.component';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
    selector: 'app-permission-set-management',
    templateUrl: './permission-set-management.component.html',
    styleUrls: ['./permission-set-management.component.scss'],
})
export class PermissionSetManagementComponent implements OnInit {
    data: any[];
    search = '';
    newPermissionMenu = false;
    isPageLoaded = false;

    permissionSetKey = 0;
    noDataType: NoDataTypes;

    // Permission Set Data
    pageData: PermissionSetPage[];
    moduleData: Module[];
    moduleDataOriginal: Module[];
    setName: string = null;
    hasName: boolean = false;

    //
    public updateData: PermissionSetUpdateForm[];
    public savingData = false;

    // Modules
    moduleTabs = [];
    moduleTab = 0;
    //

    smallScreen: boolean = false;

    constructor(
        private service: UserPermissionsService,
        private toast: ToastService,
        public Matdialog: MatDialog
    ) {}

    ngOnInit() {
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'permission sets',
            inputSource: NoDataSourceType.table,
        };

        this.onResize(null);
        this.getPermissionSets();
        this.getPageData(); // This should be moved somewhere else; Base case for new Permission Set
    }

    @HostListener('window:resize', ['$event'])
    onResize(event?) {
        let screenWidth: number = window.innerWidth;
        let maxWidth: number = 1300;

        if (screenWidth < maxWidth) {
            this.smallScreen = true;
        } else {
            this.smallScreen = false;
        }
    }

    /* Function hooked to Set Permission Name field */
    typeName() {
        // Check if permission name is taken
        this.service.getPermissionSetName(this.setName).subscribe((data) => {
            if (data.length > 0) {
                this.hasName = true;
            } else {
                this.hasName = false;
            }
        });
    }

    getPermissionSets() {
        this.service.getPermissionSets().subscribe((data) => {
            if (data) {
                this.data = data;
                this.isPageLoaded = true;
            }
        });
    }

    deletePermissionSet(set, permissionSetKey: number, permissionSetName: string) {
        const dialogRef = this.Matdialog.open(ConfirmationAlertDialogComponent, {
            data: {
                title: 'Delete Permission Set ',
                message: `Are you sure you want to permanently delete this permission set? If you're not sure, please click Cancel.`,
                acceptTerm: 'DELETE',
                rejectTerm: 'CANCEL',
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'accept') {
                set.isDeleted = true;
                this.service.deletePermissionSet(permissionSetKey).subscribe(
                    (data) => {
                        this.toast.success('Permission Set ' + permissionSetName + ' deleted.');
                        this.getPageData();
                    },
                    (error) => {
                        throw new Error(error);
                    }
                );
            }
        });
    }

    getPageData() {
        this.service.getPermissionSetPages(this.permissionSetKey).subscribe((data) => {
            if (data) {
                this.pageData = data;
                this.formatPageData();
            }
        });
    }

    // Server returns 1D array of ApplicationPage records.
    // We need to format this into a 3D array by TypeKey: Modules -> Sub Modules -> Pages
    formatPageData() {
        const pages = JSON.parse(JSON.stringify(this.pageData));
        const modules: Module[] = pages
            .filter((page: PermissionSetPage) => page.applicationPageTypeKey == 1)
            .map((mod: PermissionSetPage) => ({
                page: mod,
                pages: pages
                    .filter(
                        (subMod: PermissionSetPage) =>
                            subMod.applicationPageTypeKey == 2 &&
                            subMod.parentApplicationPageKey == mod.applicationPageKey
                    )
                    .map((subMod: PermissionSetPage) => ({
                        page: subMod,
                        pages: pages.filter(
                            (page: PermissionSetPage) =>
                                page.applicationPageTypeKey == 3 &&
                                page.parentApplicationPageKey == subMod.applicationPageKey
                        ),
                    })),
            }));

        this.moduleData = modules;
        this.moduleDataOriginal = JSON.parse(JSON.stringify(this.moduleData));

        this.moduleTabs = [];
        this.moduleData.forEach((m) => {
            this.moduleTabs.push({ label: m.page.name, icon: m.page.icon });
        });

        this.moduleTab = 0;
    }

    isActiveTab(module) {
        const tab = this.moduleTabs[this.moduleTab];
        return module === tab.label;
    }

    logTab(event: MatTabChangeEvent): void {
        this.moduleTab = event.index;
    }

    clickTab(tab: any) {
        let index: number = this.moduleTabs.findIndex((searchTab) => searchTab == tab);
        this.moduleTab = index;
    }

    //
    clickPermissionSet(set) {
        this.permissionSetKey = set.permissionSetKey;
        this.setName = set.name;
        this.editPermissionSet();
    }

    addNew() {
        this.newPermissionMenu = true;
        this.setName = null;
        this.permissionSetKey = 0;
        this.getPageData(); // This should be moved somewhere else; Base case for new Permission Set
    }

    editPermissionSet() {
        this.newPermissionMenu = true;
        this.getPageData();
    }

    exit() {
        this.updateData = [];
        this.newPermissionMenu = false;
    }

    // Determines which pages need to be updated (IsActive: true/false)
    // If parent module is selected, it will cascade down to children.
    // If any sub-module is selected, it will select parent, but not cascade down.
    clickGrantAccess(event) {
        const page = event.page;
        const pages = event.pages;

        if (!this.updateData) {
            this.updateData = [];
        }

        // Update selected row
        const newValue = !page.hasPage;
        page.hasPage = newValue;

        this.addOrRemoveFromUpdateData(page, newValue);

        // If module, iterate through sub module and sub module pages
        if (page.applicationPageTypeKey == 2) {
            const parentModule = this.moduleData.find(
                (parent) => parent.page.applicationPageKey === page.parentApplicationPageKey
            );
            if (newValue) {
                // Set parentModule == true
                this.addOrRemoveFromUpdateData(parentModule.page, true);
            } else {
                // Check other sub-modules
                const subModules = parentModule.pages;
                let anySubModulesEnabled = false;

                subModules.forEach((subModule) => {
                    if (subModule.page.hasPage) {
                        anySubModulesEnabled = true;
                    }
                });

                if (!anySubModulesEnabled) {
                    // set parent hasPage = false
                    this.addOrRemoveFromUpdateData(parentModule.page, false);
                }
            }

            if (pages) {
                // Iterate through sub modules
                pages.forEach((p) => {
                    if (p.hasPage !== newValue) {
                        p.hasPage = newValue;
                        this.addOrRemoveFromUpdateData(p, newValue);
                    }
                });
            }
        }
    }

    // Determines if value is the same as original.
    // If different, add to update array. Else, remove from update array.
    addOrRemoveFromUpdateData(page: PermissionSetPage, val: boolean) {
        const originalItem = this.pageData.find(
            (p) => p.applicationPageKey == page.applicationPageKey
        );

        // If original value is equal to new value, remove this page from the updated data array.
        if (originalItem.hasPage == val) {
            const item = this.updateData.find(
                (p) => p.applicationPageKey == page.applicationPageKey
            );
            if (item) {
                const itemIndex = this.updateData.indexOf(item);
                this.updateData.splice(itemIndex, 1);
            }
        } else if (
            this.updateData.find(
                (u) => u.applicationPageKey == page.applicationPageKey && u.isActive == val
            )
        ) {
            // ignore so the same module isn't added twice!
        } else {
            // If original not equal to new, add it to the update array.
            this.updateData.push({
                permissionSetKey: this.permissionSetKey,
                applicationPageKey: page.applicationPageKey,
                isActive: val,
            });
        }
    }

    // Send array of updated pages to server.
    update() {
        this.savingData = true;

        this.service
            .updatePermissionSetPages(this.setName, this.permissionSetKey, this.updateData)
            .subscribe((data) => {
                this.exit();
                this.toast.success(`Permissions have been updated.`);
                this.updateData = [];
                this.data = null;
                this.getPermissionSets();
                this.savingData = false;
            });
    }

    showAssignedUsers(row) {
        let permissionUsersDiaglog = this.Matdialog.open(PermissionSetUsersComponent, {
            width: '1100px',
            data: {
                permission: row,
            },
        });
    }
}
